import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  TextField,
  InputAdornment,
} from "@mui/material";
import { AttachMoney as AttachMoneyIcon } from "@mui/icons-material";
import { fetchBusiness } from 'modules/Business/actions';
import useBusinessFunding from "modules/Business/hooks/useBusinessFunding";
import {
  amountInputStep,
  getFundingAmountRules,
  validateConfiguredAmount,
} from "utils/businessPaymentRules";

const AddBalance = ({ businessId, onClose, onFundSuccess }) => {
  const dispatch = useDispatch();
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');
  const {
    addFunds,
    clearError: clearFundingError,
    errorMessage: fundingError,
    isSubmitting,
    requiresPaymentReview,
  } = useBusinessFunding(businessId);

  const business = useSelector(state => state.business);

  useEffect(() => {
    dispatch(fetchBusiness(business.businessId, true));
  }, [dispatch, business.businessId]);

  const fundingRules = getFundingAmountRules(business);
  const [amount, setAmount] = useState(null);

  const handleChange = (e) => {
    const value = e.target.value;

    clearFundingError();
    setInputValue(value);
    const validation = validateConfiguredAmount(value, fundingRules);
    setError(validation.error);
    setAmount(validation.amount);
  };

  const handleSubmit = async () => {
    if (await addFunds(amount)) {
      onClose();
      onFundSuccess?.();
    }
  };

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle sx={{ marginBottom: 2 }}>Add balance to your account</DialogTitle>
      <DialogContent>
        <FormControl variant="outlined" fullWidth>
          <TextField
            label="Amount to add"
            variant="outlined"
            type="number"
            value={inputValue}
            onChange={handleChange}
            disabled={isSubmitting || requiresPaymentReview}
            error={!!error}
            helperText={error || ' '}
            fullWidth
            inputProps={{
              min: fundingRules?.minimumAmount,
              max: fundingRules?.maximumAmount,
              step: fundingRules ? amountInputStep(fundingRules.maximumScale) : undefined,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AttachMoneyIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiInputBase-root': {
                height: '56px',
              },
              '& .MuiInputLabel-root': {
                top: '4px',
              },
            }}
          />
        </FormControl>
        {fundingError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {fundingError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Box display="flex" justifyContent="flex-end">
          <Button color="primary" onClick={onClose}>Cancel</Button>
          <Button
            color="primary"
            disabled={!amount || !!error || isSubmitting || requiresPaymentReview}
            variant="contained"
            onClick={handleSubmit}
          >
            {isSubmitting ? (
              <>
                <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} />
                Adding funds…
              </>
            ) : 'Add balance'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AddBalance;
